/**
 * @file inferBinLayout.ts
 * @description Infer the least-surprising bin layout from the current visual
 * arrangement of a selection. Clear rows, columns, and regular grids become
 * matching auto layouts; irregular compositions remain freeform so grouping
 * does not destroy intentional spatial relationships.
 */

import type { BinLayoutMode } from './binLayoutMode'
import { computeBinLayout, type BinChildInfo } from './computeBinLayout'

export interface BinLayoutInferenceNode extends BinChildInfo {
  x: number
  y: number
}

export interface InferredBinLayout {
  layoutMode: BinLayoutMode
  childOrder: string[]
  gridColumns?: number
}

interface StructuredCandidate extends InferredBinLayout {
  meanMovementScore: number
  maxMovementScore: number
}

// A quarter-card tolerance absorbs normal hand-drag jitter without flattening
// visibly staggered or diagonal compositions into an auto layout.
const CROSS_AXIS_TOLERANCE = 0.25
const MIN_PRIMARY_AXIS_SPAN = 0.5
// Auto-layout should be a close fit, not merely a structurally possible fit.
// A value of 0.65 means the average card moves by at most roughly two-thirds
// of its own diagonal. This still accepts normal hand-spaced rows while
// preserving authored whitespace and sparse compositions as freeform.
const MAX_MEAN_NORMALIZED_MOVEMENT = 0.65
// A mean alone can hide a destructive outlier: in a sparse grid, one distant
// row can be collapsed while the stationary row dilutes the average. No
// individual card should move by nearly its full diagonal when Make Bin
// chooses an automatic layout.
const MAX_CARD_NORMALIZED_MOVEMENT = 0.85
const PRIMARY_GAP_SPREAD_TOLERANCE = 0.5
// Flow grids do not have fixed columns: mixed widths change every later x in
// a row, and mixed heights vertically center cards inside each row. Admit a
// grid only when canonical materialization closely reproduces every card on
// both axes. Reusing the cross-axis tolerance keeps this at the same
// conservative quarter-card bound as the structural classifier.
const MAX_GRID_AXIS_NORMALIZED_MOVEMENT = CROSS_AXIS_TOLERANCE
const SCORE_EPSILON = 1e-6

function median(values: number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle]
}

function range(values: number[]): number {
  if (values.length === 0) return 0
  return Math.max(...values) - Math.min(...values)
}

function safeSize(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 1
}

function centerX(node: BinLayoutInferenceNode): number {
  return node.x + safeSize(node.width) / 2
}

function centerY(node: BinLayoutInferenceNode): number {
  return node.y + safeSize(node.height) / 2
}

function hasOverlappingCards(nodes: BinLayoutInferenceNode[]): boolean {
  for (let index = 0; index < nodes.length; index++) {
    const a = nodes[index]
    for (let otherIndex = index + 1; otherIndex < nodes.length; otherIndex++) {
      const b = nodes[otherIndex]
      const overlapsHorizontally = a.x < b.x + safeSize(b.width) && a.x + safeSize(a.width) > b.x
      const overlapsVertically = a.y < b.y + safeSize(b.height) && a.y + safeSize(a.height) > b.y
      if (overlapsHorizontally && overlapsVertically) return true
    }
  }
  return false
}

function gapsAreRegular(gaps: number[], medianPrimarySize: number, tolerance: number): boolean {
  if (gaps.length < 2) return true
  return range(gaps) / Math.max(1, medianPrimarySize) <= tolerance
}

function primaryGapsAreRegular(
  orderedNodes: BinLayoutInferenceNode[],
  axis: 'horizontal' | 'vertical',
  medianPrimarySize: number,
): boolean {
  if (orderedNodes.length < 2) return true

  const gaps = orderedNodes.slice(1).map((node, index) => {
    const previous = orderedNodes[index]
    return axis === 'horizontal'
      ? node.x - (previous.x + safeSize(previous.width))
      : node.y - (previous.y + safeSize(previous.height))
  })

  // Two cards provide no repeated-spacing signal: any single gap would look
  // "regular" by definition. Treat only a nearby pair as a row/column.
  // Otherwise Make Bin would collapse deliberately separated cards merely
  // because their cross-axis anchors happen to align.
  if (gaps.length === 1) {
    return gaps[0] / Math.max(1, medianPrimarySize) <= PRIMARY_GAP_SPREAD_TOLERANCE
  }

  return gapsAreRegular(gaps, medianPrimarySize, PRIMARY_GAP_SPREAD_TOLERANCE)
}

/**
 * A row may be top-, center-, or bottom-aligned (especially with mixed card
 * heights). Use the closest of those three visual anchors rather than assuming
 * identical node dimensions.
 */
function rowAlignmentSpread(nodes: BinLayoutInferenceNode[], medianHeight: number): number {
  const top = range(nodes.map(node => node.y))
  const center = range(nodes.map(centerY))
  const bottom = range(nodes.map(node => node.y + safeSize(node.height)))
  return Math.min(top, center, bottom) / Math.max(1, medianHeight)
}

/**
 * A column may likewise be left-, center-, or right-aligned.
 */
function columnAlignmentSpread(nodes: BinLayoutInferenceNode[], medianWidth: number): number {
  const left = range(nodes.map(node => node.x))
  const center = range(nodes.map(centerX))
  const right = range(nodes.map(node => node.x + safeSize(node.width)))
  return Math.min(left, center, right) / Math.max(1, medianWidth)
}

interface RowCluster {
  nodes: BinLayoutInferenceNode[]
  meanTop: number
  meanCenter: number
  meanBottom: number
  bandTop: number
  bandBottom: number
}

function overlapsRowBand(node: BinLayoutInferenceNode, row: RowCluster): boolean {
  const nodeTop = node.y
  const nodeBottom = node.y + safeSize(node.height)
  return nodeTop < row.bandBottom && nodeBottom > row.bandTop
}

function rowDistance(node: BinLayoutInferenceNode, row: RowCluster, medianHeight: number): number {
  const top = Math.abs(node.y - row.meanTop)
  const center = Math.abs(centerY(node) - row.meanCenter)
  const bottom = Math.abs(node.y + safeSize(node.height) - row.meanBottom)
  return Math.min(top, center, bottom) / Math.max(1, medianHeight)
}

function addToRow(row: RowCluster, node: BinLayoutInferenceNode): void {
  const count = row.nodes.length
  row.nodes.push(node)
  row.meanTop = (row.meanTop * count + node.y) / (count + 1)
  row.meanCenter = (row.meanCenter * count + centerY(node)) / (count + 1)
  row.meanBottom = (row.meanBottom * count + node.y + safeSize(node.height)) / (count + 1)
  row.bandTop = Math.max(row.bandTop, node.y)
  row.bandBottom = Math.min(row.bandBottom, node.y + safeSize(node.height))
}

/**
 * Cluster visual rows while tolerating the small Y jitter common after manual
 * dragging. A node must overlap the row's shared vertical band before visual
 * anchor proximity can assign it there. This keeps a short card in the next
 * canonical flow row separate even when its bottom happens to be close to a
 * preceding tall row's bottom.
 */
function clusterRows(nodes: BinLayoutInferenceNode[], medianHeight: number): BinLayoutInferenceNode[][] {
  const sorted = [...nodes].sort(
    (a, b) => centerY(a) - centerY(b) || a.y - b.y || centerX(a) - centerX(b) || a.id.localeCompare(b.id),
  )
  const rows: RowCluster[] = []

  for (const node of sorted) {
    let closest: RowCluster | null = null
    let closestDistance = Infinity
    for (const row of rows) {
      if (!overlapsRowBand(node, row)) continue
      const distance = rowDistance(node, row, medianHeight)
      if (distance < closestDistance) {
        closest = row
        closestDistance = distance
      }
    }

    if (!closest || closestDistance > CROSS_AXIS_TOLERANCE) {
      rows.push({
        nodes: [node],
        meanTop: node.y,
        meanCenter: centerY(node),
        meanBottom: node.y + safeSize(node.height),
        bandTop: node.y,
        bandBottom: node.y + safeSize(node.height),
      })
    } else {
      addToRow(closest, node)
    }
  }

  return rows
    .sort((a, b) => a.meanCenter - b.meanCenter || a.meanTop - b.meanTop)
    .map(row => [...row.nodes].sort((a, b) => centerX(a) - centerX(b) || a.x - b.x || a.id.localeCompare(b.id)))
}

function inferGrid(
  nodes: BinLayoutInferenceNode[],
  medianHeight: number,
): { childOrder: string[]; gridColumns: number } | null {
  const rows = clusterRows(nodes, medianHeight)
  if (rows.length < 2) return null

  const gridColumns = Math.max(...rows.map(row => row.length))
  if (gridColumns < 2) return null

  // Auto-layout flows row-major, so all completed rows must use the inferred
  // column count. The final row may have any non-zero number of cards,
  // matching a normal flow grid (for example 3 + 1 or 4 + 1).
  const completedRows = rows.slice(0, -1)
  if (completedRows.some(row => row.length !== gridColumns)) return null
  if (rows[rows.length - 1].length > gridColumns) return null

  return {
    childOrder: rows.flat().map(node => node.id),
    gridColumns,
  }
}

function movementScores(
  nodesById: ReadonlyMap<string, BinLayoutInferenceNode>,
  childOrder: string[],
  layoutMode: BinLayoutMode,
  gridColumns?: number,
): { mean: number; max: number; maxAxis: number } {
  const children = childOrder
    .map(id => nodesById.get(id))
    .filter((node): node is BinLayoutInferenceNode => node != null)
  const layout = computeBinLayout(children, layoutMode, null, null, gridColumns)
  if (children.length === 0) return { mean: 0, max: 0, maxAxis: 0 }

  let total = 0
  let max = 0
  let maxAxis = 0
  for (const node of children) {
    const target = layout.positions.get(node.id)
    if (!target) continue
    const deltaX = target.x - node.x
    const deltaY = target.y - node.y
    const width = safeSize(node.width)
    const height = safeSize(node.height)
    const scale = Math.max(1, Math.hypot(width, height))
    const normalizedMovement = Math.hypot(deltaX, deltaY) / scale
    total += normalizedMovement
    max = Math.max(max, normalizedMovement)
    maxAxis = Math.max(maxAxis, Math.abs(deltaX) / width, Math.abs(deltaY) / height)
  }
  return { mean: total / children.length, max, maxAxis }
}

function readingOrder(nodes: BinLayoutInferenceNode[], medianHeight: number): string[] {
  return clusterRows(nodes, medianHeight)
    .flat()
    .map(node => node.id)
}

/**
 * Infer the layout which best matches the current composition.
 *
 * The classifier is intentionally conservative: only visibly structured
 * arrangements opt into auto-layout. Everything else remains freeform, which
 * is the only choice that guarantees grouping itself does not move the work.
 */
export function inferBinLayout(nodes: BinLayoutInferenceNode[]): InferredBinLayout {
  if (nodes.length === 0) return { layoutMode: 'grid', childOrder: [] }
  if (nodes.length === 1) return { layoutMode: 'freeform', childOrder: [nodes[0].id] }

  const medianWidth = median(nodes.map(node => safeSize(node.width)))
  const medianHeight = median(nodes.map(node => safeSize(node.height)))
  // Overlap/fanning is authored spatial composition. Any auto-layout would
  // separate those cards, so preserve it as freeform even when the negative
  // gaps are perfectly regular and otherwise look row/grid-like.
  if (hasOverlappingCards(nodes)) {
    return {
      layoutMode: 'freeform',
      childOrder: readingOrder(nodes, medianHeight),
    }
  }
  const horizontalSpan = range(nodes.map(centerX)) / Math.max(1, medianWidth)
  const verticalSpan = range(nodes.map(centerY)) / Math.max(1, medianHeight)
  const rowAligned = rowAlignmentSpread(nodes, medianHeight) <= CROSS_AXIS_TOLERANCE
  const columnAligned = columnAlignmentSpread(nodes, medianWidth) <= CROSS_AXIS_TOLERANCE

  const nodesById = new Map(nodes.map(node => [node.id, node]))
  const candidates: StructuredCandidate[] = []
  const addCandidate = (candidate: StructuredCandidate): void => {
    if (
      candidate.meanMovementScore <= MAX_MEAN_NORMALIZED_MOVEMENT &&
      candidate.maxMovementScore <= MAX_CARD_NORMALIZED_MOVEMENT
    ) {
      candidates.push(candidate)
    }
  }

  if (rowAligned && horizontalSpan >= MIN_PRIMARY_AXIS_SPAN) {
    const orderedNodes = [...nodes].sort((a, b) => centerX(a) - centerX(b) || a.x - b.x || a.id.localeCompare(b.id))
    if (primaryGapsAreRegular(orderedNodes, 'horizontal', medianWidth)) {
      const childOrder = orderedNodes.map(node => node.id)
      const movement = movementScores(nodesById, childOrder, 'row')
      addCandidate({
        layoutMode: 'row',
        childOrder,
        meanMovementScore: movement.mean,
        maxMovementScore: movement.max,
      })
    }
  }

  if (columnAligned && verticalSpan >= MIN_PRIMARY_AXIS_SPAN) {
    const orderedNodes = [...nodes].sort((a, b) => centerY(a) - centerY(b) || a.y - b.y || a.id.localeCompare(b.id))
    if (primaryGapsAreRegular(orderedNodes, 'vertical', medianHeight)) {
      const childOrder = orderedNodes.map(node => node.id)
      const movement = movementScores(nodesById, childOrder, 'columns')
      addCandidate({
        layoutMode: 'columns',
        childOrder,
        meanMovementScore: movement.mean,
        maxMovementScore: movement.max,
      })
    }
  }

  const grid = inferGrid(nodes, medianHeight)
  if (grid) {
    const movement = movementScores(nodesById, grid.childOrder, 'grid', grid.gridColumns)
    // `computeBinLayout` is the source of truth for heterogeneous row-major
    // flow. Comparing its exact candidate geometry accepts canonical mixed
    // widths/heights without pretending visual columns share fixed anchors,
    // while the strict per-axis fit rejects stagger, uneven spacing, sparse
    // rows, and deliberate whitespace.
    if (movement.maxAxis <= MAX_GRID_AXIS_NORMALIZED_MOVEMENT) {
      addCandidate({
        layoutMode: 'grid',
        childOrder: grid.childOrder,
        gridColumns: grid.gridColumns,
        meanMovementScore: movement.mean,
        maxMovementScore: movement.max,
      })
    }
  }

  if (candidates.length === 0) {
    return {
      layoutMode: 'freeform',
      childOrder: readingOrder(nodes, medianHeight),
    }
  }

  candidates.sort((a, b) => {
    const scoreDifference = a.meanMovementScore - b.meanMovementScore
    if (Math.abs(scoreDifference) > SCORE_EPSILON) return scoreDifference
    // Prefer the simpler one-dimensional model on an exact score tie.
    const rank: Record<BinLayoutMode, number> = { row: 0, columns: 1, grid: 2, freeform: 3 }
    return rank[a.layoutMode] - rank[b.layoutMode]
  })

  const best = candidates[0]
  return {
    layoutMode: best.layoutMode,
    childOrder: best.childOrder,
    ...(best.gridColumns ? { gridColumns: best.gridColumns } : {}),
  }
}
